/* eslint-disable max-lines */
import { History, To } from 'history';
import throttle from 'lodash/throttle';
import { v4 as uuidv4 } from 'uuid';
import { DefaultTheme } from 'styled-components';
import { Logger } from '@elliemae/pui-diagnostics';
import {
  Events,
  ScriptingObjectTypes,
  ScriptingObjectNames,
  IApplication,
  IScriptingObjectProxy,
} from '@elliemae/pui-scripting-object';
import {
  EventListeners,
  AddScriptingObjectParams,
  DispatchEventParam,
  EventOptions,
  IScriptingObjectProxyEvent,
  ProxyEvent,
  Event,
  ScriptingObjectManager,
  ScriptingObjects,
  ScriptingObject,
  isScriptingObjectProxy,
  CallerInfo,
} from '@elliemae/microfe-common';
import { EventManager } from './eventManager.js';
import { AppInfo } from './typings/appInfo.js';
import { IMicroFEGuest } from './typings/guest.js';
import { Frame, FRAME_APP_CONTAINER_ID_PREFIX } from './frame.js';
import { getCurrentBreakpoint, getViewportSize } from './typings/window.js';
import { CAppConfig } from './config/app.js';
import { CMicroFEConfig } from './config/microFE.js';
import { IMicroFEHost, OpenAppParams } from './typings/host.js';
import { StyleLoader, ScriptLoader, ManifestLoader } from './loaders/index.js';
import { ValueOf } from './typings/common.js';
import { CMicroFEHost } from './microfeHost.js';
import { CAppRegistry } from './appRegistry.js';

const KEEP_ALIVE_INTERVAL = 120000;
const userInteractionEvents = ['click', 'scroll', 'keypress', 'touchstart'];
const cssType = /\.css$/;
const isCss = (fileName: string) => cssType.test(fileName);

type GenericFunction = (...args: unknown[]) => unknown;

type LoadOptions = AppInfo & {
  instanceId: string;
  containerId?: string;
  documentEle: Document;
  history?: History;
  theme?: DefaultTheme;
  initialRoute?: To;
  /** Pre-created host reference to attach to the guest before script execution. */
  microFEHost?: CMicroFEHost<any, any>;
  /** When true, the bridge skips calling init/mount — the guest self-initialises. */
  selfInitialize?: boolean;
  /** Pre-fetched asset manifest keyed by logical name → hashed filename. */
  manifest?: Record<string, string>;
};

type UnloadOptions = {
  id: string;
  instanceId: string;
  hostUrl: string;
  documentEle: Document;
};

type MountOptions = {
  id: string;
  instanceId: string;
};

type InitApplicationOptions = {
  id: string;
  instanceId: string;
  containerId?: string;
  name: string;
  hostUrl: string;
  manifestPath: string;
  homeRoute: string;
  initialRoute?: To;
  history?: History;
  theme?: DefaultTheme;
  microFEHost?: CMicroFEHost<any, any>;
};

/**
 * AppBridge constructor parameter
 */
export type AppBridgeParams = {
  /**
   * Base url of the application config file
   * @defaultValue document base url
   * @example https://encompass.ice.com/
   */
  appConfigBaseUrl?: string;
  /**
   * release version of the product
   * @defaultValue latest
   */
  version?: string;
  /**
   * instance of the pui diagnostics logger
   */
  logger: Logger;
  /**
   * Extend parent session when user interacts with the guest application
   * @defaultValue true
   */
  extendSession?: boolean;
};

/**
 * Information about the guest application
 */
type GuestInfo = {
  /**
   * unique application id
   */
  id: string;
  /**
   * unique instance id
   */
  instanceId: string;
  /**
   * throttled keep alive function handle
   */
  keepAlive?: ReturnType<typeof throttle>;
  /**
   * list of element ids that are added to DOM
   */
  elementIds: Array<string>;
  /**
   * guest application reference
   */
  guest: IMicroFEGuest;
  /**
   * per-guest host reference created before script injection
   */
  microFEHost?: CMicroFEHost<any, any>;
};

/**
 * AppBridge implementation. This class is responsible for creating & managing the Micro FrontEnd Guest instances. It also exposes scripting objects to all the Micro FrontEnd Guests.
 * @typeParam AppObjects - type of the scripting objects that the application supports
 * @typeParam AppEvents - type of the events that the application supports
 */
export class CAppBridge<
  AppObjects extends ScriptingObjects = Partial<ScriptingObjectTypes>,
  AppEvents extends EventListeners = Events,
> {
  /**
   * instance of the pui diagnostics logger
   */
  #logger: Logger;

  /**
   * release version of the product
   */
  #version?: string;

  /**
   * instance of the script loader
   */
  #scriptLoader: ScriptLoader;

  /**
   * instance of the style loader
   */
  #styleLoader: StyleLoader;

  /**
   * instance of the app config
   */
  #appConfig: CAppConfig;

  /**
   * instance of the micro frontend config
   */
  #microFEConfig = new CMicroFEConfig();

  /**
   * instance of the scripting object manager
   */
  #soManager: ScriptingObjectManager<AppObjects>;

  /**
   * instance of the event manager
   */
  #eventManager: EventManager<AppEvents>;

  /**
   * list of active apps
   */
  #activeApps = new Map<string, GuestInfo>();

  /**
   * host-supplied metadata per guest, keyed by app id
   */
  #guestMetadata = new Map<string, Record<string, unknown>>();

  /**
   * instance of the app registry
   */
  #appRegistry = new CAppRegistry();

  /**
   * flag to extend parent session when user interacts with the guest application
   */
  #extendSession = true;

  /**
   * Create a new instance of the AppBridge
   * @param {AppBridgeParams} options - App Bridge constructor parameters
   */
  constructor(options: AppBridgeParams) {
    const { logger } = options;
    if (!logger) throw new Error('logger is required');
    this.#logger = options.logger;
    this.#version = options.version;
    this.#extendSession = options.extendSession ?? true;
    this.#appConfig = new CAppConfig({
      version: options.version,
      baseUrl: options.appConfigBaseUrl,
    });
    this.#scriptLoader = new ScriptLoader(logger);
    this.#styleLoader = new StyleLoader(logger);
    this.#soManager = new ScriptingObjectManager<AppObjects>();
    this.#eventManager = new EventManager<AppEvents>(this.#logger);
  }

  #isFunction(value: any): value is GenericFunction {
    return typeof value === 'function';
  }

  /**
   * check if the value is a proxy event
   * @param value
   * @returns
   */
  #isProxyEvent = (
    value: any,
  ): value is IScriptingObjectProxyEvent<AppEvents> =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    value instanceof ProxyEvent ||
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    typeof value?.subscribe === 'function'; // support v1 scripting objects

  /**
   * format error message for app not found
   * @param id app id
   * @param instanceId unique instance id
   * @returns error message
   */
  #getAppNotFoundError = (id: string, instanceId: string) =>
    `Application ${id} with instance id ${instanceId} is not found. Most probably the appId property of app.config.json is not set to ${id}`;

  /**
   * remove assets from DOM
   * @param instanceId unique instance id of the app
   * @param documentEle  document element of the app
   * @returns void
   */
  #removeAssetsFromDOM = (instanceId: string, documentEle = document) => {
    const { elementIds } = this.#activeApps.get(instanceId) || {};
    if (elementIds) {
      elementIds.forEach((elementId) => {
        const ele = documentEle.getElementById(elementId);
        if (ele) ele.remove();
      });
    }
  };

  /**
   * add app to active app list
   * @param options
   * @param options.id - application id
   * @param options.instanceId - unique instance id
   * @param options.documentEle - iframe document
   * @param options.elementIds - ids of injected script/style elements
   * @param options.microFEHost - pre-created host reference for guest-initiated loading
   */
  #addAppToActiveAppList = ({
    id,
    instanceId,
    documentEle,
    elementIds,
    microFEHost,
  }: {
    id: string;
    instanceId: string;
    documentEle: Document;
    elementIds: Array<string>;
    microFEHost?: CMicroFEHost<any, any>;
  }) => {
    const app = this.#appRegistry.get({ id, instanceId });
    if (!app) {
      throw new Error(this.#getAppNotFoundError(id, instanceId));
    }
    this.#activeApps.set(instanceId, {
      id,
      instanceId,
      elementIds,
      guest: {
        guestWindow: documentEle?.defaultView as Window,
        ...app,
      },
      microFEHost,
    });
  };

  #initApplication = async ({
    id,
    instanceId,
    containerId,
    hostUrl,
    manifestPath,
    homeRoute,
    initialRoute,
    history,
    theme,
    microFEHost,
  }: InitApplicationOptions) => {
    const app = this.#appRegistry.get({ id, instanceId });
    if (!app) {
      throw new Error(
        `Application ${id} with instance id ${instanceId} is not found. Most probably the appId property of app.config.json is not set to ${id}`,
      );
    }
    if (!app.init || typeof app.init !== 'function')
      throw new Error(
        `Application ${id} with instance id ${instanceId} doesn't expose init method`,
      );

    const host =
      microFEHost ??
      new CMicroFEHost<AppObjects, AppEvents>({
        guest: { id },
        version: this.#version,
        containerId,
        logger: this.#logger,
        soManager: this.#soManager,
        eventManager: this.#eventManager,
      });

    return app.init({
      host: host as unknown as IMicroFEHost<
        Partial<ScriptingObjectTypes>,
        Events
      >,
      hostUrl,
      manifestPath,
      homeRoute,
      initialRoute,
      prevState: null,
      history,
      theme,
      hostBreakpoint: getCurrentBreakpoint(),
      hostViewportSize: getViewportSize(),
      logger: this.#logger,
    });
  };

  #loadApp = async (options: LoadOptions) => {
    try {
      // get application manifest & full asset name with hash value
      const { id, instanceId, files, name, hostUrl, documentEle, isJsModule } =
        options;
      this.#logger.debug(
        `Application ${id} with instance id ${instanceId} is loading...`,
      );
      let assets = files;
      const manifest = options.manifest ?? (await ManifestLoader.get(options));
      assets = ManifestLoader.getFullFileNameofAssets(manifest, files);
      // download & execute each asset by adding it to DOM
      // filter out css and js files and load them using respective loaders
      const cssAssets = assets.filter((fileName) => isCss(fileName));
      const jsAssets = assets.filter((fileName) => !isCss(fileName));
      const loadOptions = {
        name,
        hostUrl,
        documentEle,
        isESMModule: isJsModule,
      };
      const [styleElementIds, scriptElementIds] = await Promise.all([
        this.#styleLoader.load(cssAssets, loadOptions),
        this.#scriptLoader.load(jsAssets, loadOptions),
      ]);
      this.#addAppToActiveAppList({
        id,
        instanceId,
        documentEle,
        elementIds: [...styleElementIds, ...scriptElementIds],
        microFEHost: options.microFEHost,
      });
      if (!options.selfInitialize) {
        await this.#initApplication(options);
      }
      this.#logger.audit(
        `Application ${id} with instance id ${instanceId} loaded`,
      );
    } catch (err) {
      const message = `Application load failed. Unable to load one or more resources for ${
        options.id
      } with instance id ${options.instanceId}. ${(err as Error).message}`;
      this.#logger.error({
        message,
        appId: options.id,
        exception: err as Error,
      });
      throw new Error(message);
    }
  };

  #unloadApp = ({ id, instanceId, hostUrl, documentEle }: UnloadOptions) => {
    this.#logger.debug(
      `Application ${id} with instance id ${instanceId} unloading...`,
    );
    const app = this.#appRegistry.get({
      id,
      instanceId,
    });
    if (!app) return;
    this.#removeAssetsFromDOM(instanceId, documentEle);
    this.#scriptLoader.removeDynamicImportedScripts(hostUrl, documentEle);
    this.#scriptLoader.removePrefetchLinks(hostUrl, documentEle);
    this.#styleLoader.removeDynamicImportedStyles(hostUrl, documentEle);
    this.#appRegistry.delete({ id, instanceId });
    this.#logger.audit(
      `Application ${id} with instance id ${instanceId} unloaded`,
    );
  };

  #mountApp = async ({ id, instanceId }: MountOptions) => {
    const app = this.#appRegistry.get({ id, instanceId });
    if (!app?.mount || typeof app?.mount !== 'function')
      throw new Error(
        `Application ${id} with instance id ${instanceId} doesn't expose mount method`,
      );
    return app.mount({
      containerId: `${FRAME_APP_CONTAINER_ID_PREFIX}${id}`,
      hostBreakpoint: getCurrentBreakpoint(),
      hostViewportSize: getViewportSize(),
    });
  };

  #unmountApp = ({ id, instanceId }: MountOptions) => {
    const app = this.#appRegistry.get({ id, instanceId });
    if (!app?.unmount) return null;
    if (typeof app.unmount !== 'function')
      throw new Error(
        `Application ${id} with instance id ${instanceId} doesn't expose unmount method`,
      );
    return app.unmount({
      containerId: `${FRAME_APP_CONTAINER_ID_PREFIX}${id}`,
    });
  };

  /**
   * manage session for the guest application by calling the keepSessionAlive method exposed by parent
   * @param root0
   * @param root0.id
   * @param root0.instanceId
   */
  #manageSession = ({ id, instanceId }: { id: string; instanceId: string }) => {
    if (!this.#extendSession) return;
    try {
      // get application object
      const appObj = this.#soManager.getObject(
        ScriptingObjectNames.Application as Extract<keyof AppObjects, string>,
      ) as unknown as IApplication;
      if (!appObj) {
        this.#logger.warn({
          message: `Application scripting object not available for ${id} to manage session`,
        });
        return;
      }
      const app = this.#activeApps.get(instanceId);
      if (!app) return;
      app.keepAlive = throttle(
        async () => {
          try {
            await appObj.keepSessionAlive();
          } catch (e) {
            this.#logger.error(
              `Error keeping session alive. ${(e as Error).message}`,
            );
          }
        },
        KEEP_ALIVE_INTERVAL, // throttle time
      );
      const frameEle = Frame.get(instanceId);
      userInteractionEvents.forEach((eventType) => {
        frameEle?.contentDocument?.addEventListener(eventType, app.keepAlive!);
      });
    } catch (err) {
      this.#logger.warn({
        message: `Application scripting object not available for ${id} to manage session`,
        exception: err as Error,
      });
    }
  };

  /**
   * If the guest's module SO exposes _setUnloadHandler (duck-typed),
   * bind it to closeApp so the guest can trigger its own teardown.
   * @param id
   * @param instanceId
   */
  #bindModuleUnload = (id: string, instanceId: string) => {
    const moduleSO = this.#soManager.getObject(
      ScriptingObjectNames.Module as Extract<keyof AppObjects, string>,
      { id },
    );
    // duck-type check: the module SO may optionally expose this handler
    const so = moduleSO as unknown as Record<string, unknown> | undefined;
    const handler = so?._setUnloadHandler; // eslint-disable-line no-underscore-dangle
    if (typeof handler === 'function') {
      (handler as (cb: () => Promise<void>) => void)(() =>
        this.closeApp(instanceId),
      );
    }
  };

  /**
   * Dispatch the module.unloading event and collect feedback.
   * Returns true if unload is allowed, false if any listener vetoed.
   * @param id
   */
  #canUnload = async (id: string): Promise<boolean> => {
    const moduleSO = this.#soManager.getObject(
      ScriptingObjectNames.Module as Extract<keyof AppObjects, string>,
      { id },
    );
    if (!moduleSO) return true;
    try {
      /* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any */
      const feedback = await this.#eventManager.dispatchEvent(moduleSO as any, {
        event: { id: 'module.unloading' as any, name: 'unloading' },
        eventParams: { moduleId: id } as any,
        eventOptions: { timeout: 1000 } as any,
      });
      /* eslint-enable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any */
      if (Array.isArray(feedback) && feedback.some((v) => v === false)) {
        this.#logger.info(
          `Unload denied by guest ${id} via module.unloading event`,
        );
        return false;
      }
    } catch (err) {
      this.#logger.warn({
        message: `Error dispatching module.unloading event for ${id}`,
        exception: err as Error,
      });
    }
    return true;
  };

  /**
   * clear session management for the guest application
   * @param instanceId unique instance id of the application
   * @param app
   * @returns
   */
  #clearSession = (app: GuestInfo) => {
    if (!this.#extendSession || !app) return;
    const { keepAlive, instanceId } = app;
    if (keepAlive) {
      const frameEle = Frame.get(instanceId);
      const targetDoc = frameEle?.contentDocument;
      if (targetDoc) {
        userInteractionEvents.forEach((eventType) => {
          targetDoc.removeEventListener(eventType, keepAlive);
        });
      }
      app.keepAlive?.cancel();
    }
  };

  /**
   * registers scripting object to the host
   * @param {ValueOf<AppObjects>} so scripting object
   * @param {AddScriptingObjectParams} params params to add scripting object
   */
  addScriptingObject = <SO extends ValueOf<AppObjects>>(
    so: SO,
    params?: AddScriptingObjectParams,
  ) => {
    // if scripting object is a proxy object, clone it
    if (isScriptingObjectProxy(so)) {
      const clonedSo = this.cloneScriptingObject(so);
      this.#soManager.addScriptingObject(clonedSo, params);
    } else {
      // if scripting object is not a proxy object, add it directly
      this.#soManager.addScriptingObject(so, params);
    }
  };

  /**
   * Create new Scripting Object from SSF scripting object proxy
   * @param proxy - reference to the scripting object obtained through getObject method
   * @returns cloned version of the scripting object
   */
  cloneScriptingObject = <ObjectId extends Extract<keyof AppObjects, string>>(
    proxy: IScriptingObjectProxy,
  ): AppObjects[ObjectId] => {
    if (!proxy) throw new Error('proxy is required');
    // Create a new, base ScriptingObject
    const so = new ScriptingObject(proxy.id, proxy.objectType);
    type UnsubscribeFn = () => void;
    // Store the tokens for unsubscription
    let unsubscribers: Array<UnsubscribeFn> = [];

    // Copy the functions and events over
    // let propName: keyof typeof proxy;
    Object.keys(proxy).forEach((propName) => {
      const propValue = proxy[propName as keyof IScriptingObjectProxy];
      if (this.#isProxyEvent(propValue)) {
        // v1 or v2 object event
        const event = new Event<AppEvents>({
          name: propValue.name || propName,
          objectId: so.id,
        });
        // Create the event on the object
        Object.defineProperty(so, propName, {
          value: event,
          enumerable: true,
        });

        // Subscribe to the event and pass the callback down
        const listener = ({
          eventParams,
          eventOptions,
        }: {
          eventParams: any;
          eventOptions?: any;
        }) =>
          this.dispatchEvent({
            event,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            eventParams,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            eventOptions,
          });

        const token = (propValue as unknown as ProxyEvent).subscribe(listener);
        unsubscribers.push(() => {
          (propValue as unknown as ProxyEvent).unsubscribe(token);
        });
      } else if (this.#isFunction(propValue)) {
        // Create a function that proxies the call to the proxy
        Object.defineProperty(so, propName, {
          value: async (...args: unknown[]) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const callerCtx = (so as any)[propName]?.callContext;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (callerCtx?.guest) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              const existingChain = (callerCtx.callChain ?? []) as CallerInfo[];
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              const guestId = callerCtx.guest.id as string;
              const meta = this.#guestMetadata.get(guestId);
              const callerEntry: Record<string, unknown> = { id: guestId };
              if (meta) callerEntry.metadata = meta;
              Object.defineProperty(propValue, 'callContext', {
                value: {
                  callChain: [...existingChain, callerEntry],
                },
                configurable: true,
                enumerable: true,
                writable: true,
              });
            }
            const retVal = await propValue(...args);
            return isScriptingObjectProxy(retVal)
              ? this.cloneScriptingObject(retVal)
              : retVal;
          },
          enumerable: true,
        });

        // Override the dispose function
        if (propName === 'dispose') {
          // eslint-disable-next-line @typescript-eslint/unbound-method
          const defaultImpl = so.dispose;
          Object.defineProperty(so, propName, {
            value: () => {
              // Dispose of the object in the proxy tier
              // eslint-disable-next-line no-underscore-dangle
              so._dispose();
              return defaultImpl.apply(so);
            },
            enumerable: true,
          });
        }
      }
    });

    // Override the dispose function// Add a destroy method to clean up the proxy by unsubscribing to the
    // proxied object's events and unpublishing the object
    // eslint-disable-next-line no-underscore-dangle
    so._dispose = () => {
      unsubscribers.forEach((unsub) => {
        unsub?.();
      });
      unsubscribers = [];
    };

    return so as unknown as AppObjects[ObjectId];
  };

  /**
   * Close all active guest micro frontend applications
   */
  closeAllApps = async () => {
    await Promise.all([...this.#activeApps.keys()].map(this.closeApp));
  };

  /**
   * Close guest micro frontend application
   * @param instanceId unique instance id of the application
   */
  // eslint-disable-next-line max-statements
  closeApp = async (instanceId: string) => {
    if (!instanceId) throw new Error('instanceId is required');
    const app = this.#activeApps.get(instanceId);
    if (!app) {
      this.#logger.warn(
        `Application with instance id ${instanceId} is not found`,
      );
      return;
    }
    const { id } = app;

    if (!(await this.#canUnload(id))) return;

    const appConfig = this.#microFEConfig.getConfigById(id);
    if (!appConfig) {
      this.#logger.warn(`Configuration for application ${id} is not found`);
      this.#activeApps.delete(instanceId);
      Frame.remove(instanceId);
      return;
    }
    const { hostUrl } = appConfig;
    try {
      this.#clearSession(app);
      // unmount the app
      await this.#unmountApp({ id, instanceId });
    } finally {
      const frameEle = Frame.get(instanceId);
      if (frameEle?.contentDocument) {
        this.#unloadApp({
          id,
          instanceId,
          hostUrl,
          documentEle: frameEle.contentDocument,
        });
      }
      this.#activeApps.delete(instanceId);
      this.#guestMetadata.delete(id);
      Frame.remove(instanceId);
    }
  };

  /**
   * dispatch event to guest microfrontend application
   * @param {DispatchEventParams<EventId, Params, Options>} params - event parameters
   */
  dispatchEvent = async <
    EventId extends Extract<keyof AppEvents, string>,
    Params extends Parameters<AppEvents[EventId]>[0]['eventParams'],
    Options extends EventOptions,
  >(
    params: DispatchEventParam<EventId, Params, Options>,
  ) => {
    const {
      event: { id, name },
      eventOptions = {} as Options,
    } = params;
    const { guestId } = eventOptions;
    if (!id) throw new Error('Event Id is required');
    // Get the scripting object
    const objectId = id.split('.')?.[0] as Extract<keyof AppObjects, string>;
    const scriptingObject = guestId
      ? this.#soManager.getObject(objectId, { id: guestId })
      : this.#soManager.getObject(objectId);
    if (!scriptingObject) {
      this.#logger.warn(
        `Attempt to dispatch event ${name} on unknown object ${objectId}`,
      );
      return Promise.resolve();
    }
    return this.#eventManager.dispatchEvent(scriptingObject, params);
  };

  /**
   * Get App by instanceId
   * @param id unique id of guest
   * @param instanceId
   * @returns guest instance
   */
  getApp = <T extends IMicroFEGuest>(instanceId: string) =>
    this.#activeApps.get(instanceId)?.guest as T;

  /**
   * Get list of active apps
   * @returns list of active guests
   */
  getApps = () => [...this.#activeApps.values()].map((app) => app.guest);

  /**
   * Initialize appBridge.
   * @param preloadedAppConfig - Already-parsed config object. When provided
   *   the bridge skips its own HTTP fetch of app.config.json.
   */
  init = async (preloadedAppConfig?: Record<string, unknown>) => {
    if (preloadedAppConfig) {
      this.#appConfig.setPreloadedConfig(preloadedAppConfig);
    } else {
      await this.#appConfig.load();
    }
    this.#microFEConfig.init({
      version: this.#version,
      appConfig: this.#appConfig,
    });
    this.#injectPreconnects();
  };

  /**
   * Inject `<link rel="preconnect">` tags into the parent document for each
   * unique micro-app origin. Called once at the end of {@link init} so that
   * DNS/TCP/TLS handshakes start before any iframe is created.
   */
  #injectPreconnects = () => {
    const apps: Record<string, unknown> =
      this.#appConfig.get('microFrontendApps') ?? {};
    const origins = new Set<string>();
    Object.keys(apps).forEach((id) => {
      const cfg = this.#microFEConfig.getConfigById(id);
      if (cfg?.hostUrl) {
        try {
          origins.add(new URL(cfg.hostUrl).origin);
        } catch {
          // invalid URL — skip
        }
      }
    });
    origins.forEach((origin) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = origin;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  };

  /**
   * Mount guest micro frontend application into DOM
   * @param instanceId unique instance id of guest micro frontend application
   * @throws Error if application with given instance id is not found in configuration
   */
  mountApp = async (instanceId: string) => {
    if (!instanceId) throw new Error('instanceId is required');
    const { id } = this.#activeApps.get(instanceId) || {};
    if (!id) {
      throw new Error(
        `Application with instance id ${instanceId} is not found`,
      );
    }
    const appConfig = this.#microFEConfig.getConfigById(id);
    if (!appConfig) {
      throw new Error(`Application with id ${id} is not found`);
    }
    await this.#mountApp({ ...appConfig, instanceId });
  };

  /**
   * Prepare the iframe: create a CMicroFEHost, expose it on window.emui,
   * and inject a preconnect hint for the guest origin.
   * @param frameDoc
   * @param appConfig
   * @param appConfig.hostUrl
   * @param options
   * @param options.id
   * @param options.containerId
   */
  #setupIframe = (
    frameDoc: Document,
    appConfig: { hostUrl: string },
    options: { id: string; containerId?: string },
  ): CMicroFEHost<AppObjects, AppEvents> => {
    const microFEHost = new CMicroFEHost<AppObjects, AppEvents>({
      guest: { id: options.id },
      version: this.#version,
      containerId: options.containerId,
      logger: this.#logger,
      soManager: this.#soManager,
      eventManager: this.#eventManager,
    });

    const iframeWindow = frameDoc.defaultView;
    if (iframeWindow) {
      iframeWindow.emui = iframeWindow.emui ?? {};
      // eslint-disable-next-line no-underscore-dangle
      iframeWindow.emui.__host = microFEHost as unknown as IMicroFEHost;
      iframeWindow.emui.getHost = () =>
        // eslint-disable-next-line no-underscore-dangle
        (iframeWindow.emui?.__host as IMicroFEHost) ?? null;
    }

    const preconnect = frameDoc.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = appConfig.hostUrl;
    preconnect.crossOrigin = 'anonymous';
    frameDoc.head.appendChild(preconnect);

    return microFEHost;
  };

  /**
   * Open guest micro frontend application
   * @param {OpenAppParams} params - options to open guest application
   */
  // eslint-disable-next-line max-statements
  openApp = async (params: OpenAppParams) => {
    const {
      id,
      frameOptions,
      history,
      theme,
      homeRoute,
      initialRoute,
      metadata,
      selfInitialize,
    } = params;
    const instanceId = uuidv4();
    if (metadata) this.#guestMetadata.set(id, metadata);
    const appConfig = this.#microFEConfig.getConfigById(id);
    if (!appConfig) {
      throw new Error(`Application ${id} is not found in app config`);
    }
    const manifestPromise = ManifestLoader.get(appConfig);
    manifestPromise.catch(() => {});
    const frameEle = await Frame.create({
      id,
      instanceId,
      manifestPath: appConfig.manifestPath,
      hostUrl: appConfig.hostUrl,
      options: { title: appConfig.name, ...frameOptions },
    });
    if (!frameEle?.contentDocument)
      throw new Error('unable to create iframe for the microapp');
    try {
      this.#appRegistry.add({
        id,
        instanceId,
        documentEle: frameEle.contentDocument,
      });
      const microFEHost = this.#setupIframe(
        frameEle.contentDocument,
        appConfig,
        {
          id,
          containerId: frameOptions?.containerId,
        },
      );
      const manifest = await manifestPromise;
      await this.#loadApp({
        instanceId,
        history,
        theme,
        documentEle: frameEle.contentDocument,
        containerId: frameOptions?.containerId,
        ...appConfig,
        homeRoute: homeRoute ?? appConfig.homeRoute,
        initialRoute,
        microFEHost,
        selfInitialize,
        manifest,
      });
      if (!selfInitialize) {
        await this.#mountApp({ instanceId, ...appConfig });
      }
      this.#manageSession({ id, instanceId });
      this.#bindModuleUnload(id, instanceId);
      return instanceId;
    } catch (err) {
      this.#unloadApp({
        id,
        instanceId,
        hostUrl: appConfig.hostUrl,
        documentEle: frameEle.contentDocument,
      });
      Frame.remove(instanceId);
      throw err;
    }
  };

  /**
   * remove all listeners
   */
  removeAllEventSubscriptions = () => {
    this.#eventManager.unsubscribeAll();
  };

  /**
   * removes all scripting objects from host
   * @param guestId unique id of the guest application
   */
  removeAllScriptingObjects = (guestId?: string) => {
    this.#soManager.removeAllScriptingObjects(guestId);
  };

  /**
   * removes scripting object from the host
   * @param objectId unique id of the scripting object
   * @param guestId
   */
  removeScriptingObject = (
    objectId: Extract<keyof AppObjects, string>,
    guestId?: string,
  ) => {
    this.#soManager.removeScriptingObject(objectId, guestId);
  };

  /**
   * Pre-fetch the manifest for a guest application so that a subsequent
   * {@link openApp} call can skip the manifest network round-trip.
   * Safe to call multiple times — results are cached.
   * @param id application id as defined in app.config.json
   */
  warmUp = (id: string): void => {
    const appConfig = this.#microFEConfig.getConfigById(id);
    if (!appConfig) return;
    ManifestLoader.get(appConfig).catch(() => {});
  };

  /**
   * Unmount guest micro frontend application from DOM
   * @param instanceId unique instance id of guest micro frontend application
   * @throws Error if application with given id is not found in configuration
   */
  unmountApp = async (instanceId: string) => {
    if (!instanceId) throw new Error('instanceId is required');
    const { id } = this.#activeApps.get(instanceId) || {};
    if (!id) {
      throw new Error(
        `Application with instance id ${instanceId} is not found`,
      );
    }
    const appConfig = this.#microFEConfig.getConfigById(id);
    if (!appConfig) {
      throw new Error(`Application with id ${id} is not found`);
    }
    await this.#unmountApp({ ...appConfig, instanceId });
  };
}
